import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { EventApi } from '@fullcalendar/core/api/EventApi';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import { ResourceSourceInput } from '@fullcalendar/resource-common/structs/resource-source';
import resourceTimeGridDayPlugin from '@fullcalendar/resource-timegrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import * as moment from 'moment';
import { from, of, Subject, Subscription } from 'rxjs';
import { concatMap, delay, takeUntil, takeWhile } from 'rxjs/operators';
import { GetLeadEventsFilter } from '../../../../shared/models/calendar/get-lead-events.filter';
import { GetLeadEventsInput } from '../../../../shared/models/calendar/get-lead-events.input';
import { RemoveEventInput } from '../../../../shared/models/calendar/remove-event.input';
import { Company } from '../../../../shared/models/company/company.model';
import { EventType } from '../../../../shared/models/types/event.types';
import { RolType } from '../../../../shared/models/types/rol.types';
import { CalendarComuService } from '../../../../shared/services/calendar/calendar-comu.service';
import { CalendarDataService } from '../../../../shared/services/calendar/calendar-data.service';
import { CalendarService } from '../../../../shared/services/calendar/calendar.service';
import { CompanyService } from '../../../../shared/services/company/company.service';
import { ToastService } from '../../../../shared/services/utils/toast.service';
import { TranslatorService } from '../../../../shared/services/utils/translator.service';
import { LeadsComunicationService } from '../../../leads/leads-comunication.service';
import { CompanySelectionDropdownComponent } from '../../dropdown/company-selection-dropdown/company-selection-dropdown.component';
import { DropDownService } from '../../dropdown/dropdown.service';
import { UserSelectionByRoleDropdownComponent } from '../../dropdown/user-selection-by-role-dropdown/user-selection-by-role-dropdown.component';
import { AuthService } from './../../../../shared/services/security/auth.service';

@Component({
  selector: 'ngx-calendar-component',
  templateUrl: './calendar-basic.component.html',
  styleUrls: [],
})
export class CalendarBasicComponent implements OnInit, OnDestroy {
  @ViewChild(UserSelectionByRoleDropdownComponent, { static: false })
  userSelectionComponent: UserSelectionByRoleDropdownComponent;
  @ViewChild(CompanySelectionDropdownComponent, { static: false })
  companySelectionComponent: CompanySelectionDropdownComponent;
  @ViewChild('fullcalendar', { static: true })
  fullcalendar: FullCalendarComponent;
  @ViewChild('external', { static: true }) external: ElementRef;

  @Input() leadId: string;
  @Input() inCall: boolean;
  @Input() isNotPlanning: boolean;
  @Input() isPlanningDay: boolean;
  @Input() isPrequalificationCompetitive = false;
  calendarPlugins = [
    dayGridPlugin,
    interactionPlugin,
    timeGridPlugin,
    resourceTimeGridDayPlugin,
  ];
  enlargeCalendar: boolean;
  userId: string;
  eventAdded: boolean;
  isGridDay: boolean;
  isGridWeek: boolean;
  rolType = new RolType();
  comment: any;
  calendarForm: FormGroup;
  getDataCalendarSubscribe: any;
  alive = true;
  eventTypes: EventType[];
  sizeCalendar: string[];
  userLoaded: boolean;
  companyLoaded: boolean;
  itemsShowLimit: number;
  searchEventsFinish: boolean;
  renderSuscriber;
  destroyed = new Subject<void>();
  calendarLocation: any;
  isPlanDay: boolean;
  usersSelected;
  companiesSelected;
  companies: Company[];
  multipleCompanies = false;
  roleUser;
  filterForPlanningEvents: GetLeadEventsFilter;
  eventRemovedSubcription: Subscription;

  constructor(
    private readonly translatorService: TranslatorService,
    readonly authService: AuthService,
    private readonly formBuilder: FormBuilder,
    private readonly calendarService: CalendarService,
    private readonly calendarComuService: CalendarComuService,
    private readonly leadComuService: LeadsComunicationService,
    private readonly toastService: ToastService,
    private readonly calendarDataService: CalendarDataService,
    private readonly dropDownService: DropDownService,
    private readonly router: Router,
    private readonly companyService: CompanyService,
  ) { this.filterForPlanningEvents = new GetLeadEventsFilter(); }
  ngOnDestroy(): void {
    this.alive = false;
    this.destroyed.next();
    if (this.eventRemovedSubcription) {
      this.eventRemovedSubcription.unsubscribe();
    }
  }
  ngOnInit(): void {
    this.roleUser = this.authService.getUserRol();
    this.userLoaded = false;
    this.companyLoaded = false;
    this.calendarForm = this.formBuilder.group({ comment: '' });
    this.isGridDay = true;
    this.usersSelected = this.authService.getUser();
    this.companiesSelected = this.authService.getUser().company;
    this.companyService.getUserLoggedCompanies$().subscribe(res => {
      this.companies = res.companies;
      if (this.companies.length > 1) {
        this.multipleCompanies = true;
      }
    });
    this.calendarService
      .getEventTypes()
      .pipe(takeWhile(() => this.alive))
      .subscribe((eventTypes) => (this.eventTypes = eventTypes.eventTypes));
    this.fullcalendar = this.calendarDataService.getCalendar(
      this.fullcalendar,
      this.authService.getUserRol(),
      this.inCall,
      this.isNotPlanning
    );
    this.dropDownService.getUserCompleted
      .pipe(takeWhile(() => this.alive))
      .subscribe(() => {
        this.searchEvents();
        this.userLoaded = true;
      });
    this.dropDownService.getCompanyCompleted
      .pipe(takeWhile(() => this.alive))
      .subscribe(() => {
        this.companyLoaded = true;
      });
    this.fullcalendar.datesRender.subscribe(() => {
      if (this.userLoaded || this.companyLoaded || (this.isRoleUserOrBdcInPlanning()) || this.inCall) {
        this.searchEvents();
      }
    });
    this.fullcalendar.eventReceive.subscribe((event) =>
      this.addEvent(event.event)
    );
    this.fullcalendar.eventRender.subscribe((event) => {
      this.addButtonRemoveIfPosible(event);
    });
    this.createButtons();
    if (this.isNotPlanning) {
      this.createEventsDraggables();
    } else {
      this.itemsShowLimit = 1;
      this.external.nativeElement.style.display = 'none';
    }
    this.calendarComuService.getDataCalendarEvent
      .pipe(takeWhile(() => this.alive))
      .subscribe(() => {
        let eventAdded: EventApi;
        eventAdded = this.fullcalendar
          .getApi()
          .getEventById(EventType.REMINDER);
        if (!eventAdded) {
          eventAdded = this.fullcalendar.getApi().getEventById(EventType.VISIT);
        }

        if (!eventAdded) {
          this.calendarComuService.returnedCalendarData.emit(false);
        } else {
          const dataReturned = {
            comment: this.comment,
            dateFrom: moment(eventAdded.start),
            leadId: this.leadId,
            userId: eventAdded.getResources()[0]
            ? eventAdded.getResources()[0].id
            : this.authService.getUser().id,
            type: eventAdded.id,
          };
          this.calendarComuService.returnedCalendarData.emit(dataReturned);
        }
      });

    this.eventRemovedSubcription = this.calendarComuService.eventRemoved.subscribe((eventRemovedInput) => {
      const eventRendered = this.fullcalendar.getApi().getEventById(eventRemovedInput.eventId);
      if (eventRemovedInput.leadId === this.leadId) {
        const eventRemovedInputData = new RemoveEventInput();
        eventRemovedInputData.eventId = eventRendered.id;
        eventRemovedInputData.leadId = this.leadId;
        this.calendarService
          .removeEvent$(eventRemovedInputData)
          .pipe(takeWhile(() => this.alive))
          .subscribe(() => {
            eventRendered.remove();
            this.calendarComuService.activateInteraction.emit(true);
            this.toastService.showToast(
              'success',
              'toaster.title.lead_remove_event_success',
              'toaster.lead_remove_event_success'
            );
          });
      }
    });
  }

  searchEvents(): void {
    if (this.renderSuscriber) {
      this.renderSuscriber.unsubscribe();
    }

    const dateTemp = this.fullcalendar.getApi().view;
    const cd = moment(this.fullcalendar.getApi().getDate());
    const input = new GetLeadEventsInput();
    input.leadId = this.leadId;
    const filter = new GetLeadEventsFilter();

    if (
      this.userSelectionComponent &&
      this.userSelectionComponent.selectedItems.length > 0
    ) {
      filter.userIds = this.userSelectionComponent.selectedItems.map((el) => el.id);
    } else if (
      this.userSelectionComponent &&
      this.userSelectionComponent.selectedItems.length === 0
    ) {
      filter.userIds = this.userSelectionComponent.itemsData.map((el) => el.id);
    } else if (
      this.inCall
    ) {
      filter.userIds = [this.authService.getUser().id];
    }
    if (this.multipleCompanies) {
      filter.companyIds = this.companies.map(company => company.id);
    }
    if (
      this.companySelectionComponent &&
      this.companySelectionComponent.selectedItems.length > 0
    ) {
      filter.companyIds = this.companySelectionComponent.selectedItems.map((el) => el.id);
    } else if (
      this.companySelectionComponent &&
      this.companySelectionComponent.selectedItems.length === 0
    ) {
      filter.companyIds = this.companySelectionComponent.itemsData.map((el) => el.id);
    }

    if (!this.isNotPlanning && !this.isPlanningDay) {
      filter.leadId = input.leadId;
      this.calendarComuService.searchEvents.emit(filter);
    }

    if (dateTemp.type === 'resourceTimeGridWeek' || dateTemp.type === 'timeGridWeek') {

      filter.dateFrom = cd.startOf('week').toDate();
      filter.dateTo = cd.endOf('week').toDate();
    } else {

      filter.dateFrom = cd.startOf('day').toDate();
      filter.dateTo = cd.endOf('day').toDate();
    }

    input.filter = filter;
    if (this.isPlanningDay) {
      if (!this.multipleCompanies) {
        filter.companyIds = [this.authService.getUser().company.id];
      }
      this.calendarComuService.searchPlanningDayEvents.emit(filter);
    }
    if (this.isNotPlanning) {
      this.calendarLocation = this.calendarService.getEvents$(input);
    } else {
      this.calendarLocation = this.calendarService.getEventsByCompanies$(input);

    }

    this.calendarLocation.pipe(takeUntil(this.destroyed)).subscribe((res) => {
      this.cleanEvents();
      this.cleanResources();
      let resources = [];
      this.fullcalendar
        .getApi()
        .addEventSource(this.calendarDataService.mapLeadinEvents(res.events));
      if (this.userSelectionComponent) {
        if (this.userSelectionComponent.selectedItems.length > 0) {
          resources = this.createResourcesObject(
            this.userSelectionComponent.selectedItems,
            res.businessHours
          );
        } else {
          resources = this.createResourcesObject(
            this.userSelectionComponent.itemsData,
            res.businessHours
          );
        }
      }
      this.renderSuscriber = from(resources).pipe(concatMap((val) => of(val).pipe(delay(50)))).subscribe((resource) => {
        this.addResources(resource);
        this.getSizeCalendar(
          resources.length,
          dateTemp.type
        );
        this.fullcalendar.getApi().rerenderResources();
        this.fullcalendar.getApi().rerenderEvents();
      });
    });
  }

  addEvent(event: EventApi): void {
    if (event.start > new Date()) {
      this.external.nativeElement.style.display = 'none';
      this.eventAdded = true;
      this.calendarComuService.eventAdded.emit();

      this.filterForPlanningEvents = new GetLeadEventsFilter();

      this.filterForPlanningEvents.dateFrom = moment(Date.now()).toDate();
      this.filterForPlanningEvents.dateTo = moment(event.start).toDate();

      this.getPlanningEvents(this.filterForPlanningEvents);
    } else {
      this.fullcalendar.getApi().getEventById(event.id).remove();
      this.toastService.showToast('error', 'errors.event_no_added', 'errors.event_should_date_greater_that_current');
    }
  }

  searchEventsSameTypeAndSameLead(type: string, events: any[]): EventApi {
    const eventActualLead = events.filter(event => event.extendedProps.type === type && event.extendedProps.leadId === this.leadId)[0];

    return eventActualLead;
  }

  removeEventAdded(): void {
    this.eventAdded = false;
    if (this.isNotPlanning) {
      this.external.nativeElement.style.display = 'flex';
    }
    this.searchEvents();
    const customPlanningFilter = this.filterForPlanningEvents;
    customPlanningFilter.dateTo = null;
    this.getPlanningEvents(customPlanningFilter);
    this.calendarComuService.removeEvent.emit();
  }

  getSizeCalendar(numberUser, typeView): void {
    this.sizeCalendar = [];

    if (typeView !== 'timeGridDay' && typeView !== 'resourceTimeGridDay') {
      if (numberUser > 3) {
        this.sizeCalendar = ['calendar-scrollable', 'size-200'];
      }
      if (numberUser > 5) {
        this.sizeCalendar = ['calendar-scrollable', 'size-300'];
      }
      if (numberUser > 8) {
        this.sizeCalendar = ['calendar-scrollable', 'size-400'];
      }
      if (numberUser > 12) {
        this.sizeCalendar = ['calendar-scrollable', 'size-500'];
      }
      if (numberUser > 16) {
        this.sizeCalendar = ['calendar-scrollable', 'size-600'];
      }
    } else if (
      typeView === 'timeGridDay' || typeView === 'resourceTimeGridDay'
    ) {
      if (numberUser > 10) {
        this.sizeCalendar = ['calendar-scrollable', 'size-200'];
      }
      if (numberUser > 20) {
        this.sizeCalendar = ['calendar-scrollable', 'size-300'];
      }
    }
  }

  private createResourcesObject(userIds: any[], businessHours: any[]) {
    return userIds.map((item) => {
      const resourceObject: any = {};
      resourceObject.id = item.id;
      resourceObject.name = item.name;
      if (this.authService.isTestAB()) {
        resourceObject.businessHours = businessHours.filter(
          (businessHourByUser) => businessHourByUser.userId === item.id
            && businessHourByUser.startTime && businessHourByUser.endTime
        );
      }

      return resourceObject;
    });
  }

  private addButtonRemoveIfPosible(event: any): void {
    if (!event.event.extendedProps.temporal && event.event.extendedProps.leadId === this.leadId && event.event.start > new Date()) {
      const closeButtonContainer = document.createElement('div');
      closeButtonContainer.style.position = 'absolute';
      closeButtonContainer.style.top = '5px';
      closeButtonContainer.style.right = '5px';
      closeButtonContainer.style.cursor = 'pointer';
      closeButtonContainer.innerHTML = `<span class='closeon'><i style='font-size:13px' class='fas fa-trash'></i></span>`;
      closeButtonContainer.addEventListener('click', () => this.removeEvent(event.event.id, event.event.extendedProps.leadId));
      event.el.firstChild.appendChild(closeButtonContainer);
    }
  }

  private removeEvent(id: string, leadId: string): void {
    const eventRendered = this.fullcalendar.getApi().getEventById(id);
    if (leadId === this.leadId) {
      eventRendered.remove();
      const eventRemovedInput = new RemoveEventInput();
      eventRemovedInput.eventId = eventRendered.id;
      eventRemovedInput.leadId = this.leadId;
      this.calendarService
        .removeEvent$(eventRemovedInput)
        .pipe(takeWhile(() => this.alive))
        .subscribe(() => {
          eventRendered.remove();
          this.calendarComuService.activateInteraction.emit(true);
          this.leadComuService.launchReloadMyLead();
          this.toastService.showToast(
            'success',
            'toaster.title.lead_remove_event_success',
            'toaster.lead_remove_event_success'
          );
        });
    }
  }

  private createEventsDraggables(): void {
    // tslint:disable-next-line: no-unused-expression
    new Draggable(this.external.nativeElement, {
      itemSelector: '.fc-event',
      eventData: (eventEl) => ({
        id: eventEl.getAttribute('data-event-type'),
        title: eventEl.innerText,
        duration:
          eventEl.getAttribute('data-event-type') === EventType.REMINDER
            ? '00:05:00'
            : '00:30:00',
        textColor: '#FFF',
        backgroundColor: eventEl.getAttribute('data-event-color'),
        borderColor: eventEl.getAttribute('data-event-color'),
        resourceEditable: true,
        extendedProps: {
          type: eventEl.getAttribute('data-event-type'),
          leadId: this.leadId,
          temporal: true,
        },
      }),
    });
  }

  private createButtons(): void {
    this.fullcalendar.customButtons = {
      customResourceTimeGridDay: {
        text: 'Dia',
        click: () => {
          this.isGridDay = true;
          this.isGridWeek = false;
          this.fullcalendar.getApi().changeView('resourceTimeGridDay');
        },
      },
      customResourceTimeGridWeek: {
        text: 'Semana',
        click: () => {
          this.isGridDay = false;
          this.isGridWeek = true;
          this.fullcalendar.getApi().changeView('resourceTimeGridWeek');
        },
      },
      customToday: {
        text: this.translatorService.trans('pages.leads.today'),
        click: () => {
          this.fullcalendar.getApi().today();
        },
      },
      customNext: {
        text: '',
        icon: 'chevron-right',
        click: () => {
          this.fullcalendar.getApi().next();
        },
      },
      customPrev: {
        text: '',
        icon: 'chevron-left',
        click: () => {
          this.removeEventAdded();
          this.fullcalendar.getApi().prev();
        },
      },
    };
  }

  private cleanEvents(): void {
    this.fullcalendar.getApi().removeAllEvents();
  }

  private cleanResources(): void {
    const resources = this.fullcalendar.getApi().getResources();
    resources.forEach((element) =>
      this.fullcalendar.getApi().getResourceById(element.id).remove()
    );
  }

  private addResources(element): void {
    this.fullcalendar.getApi().addResource({
      id: element.id,
      title: element.name,
      businessHours: element.businessHours,
    } as ResourceSourceInput);
  }

  private isRoleUserOrBdcInPlanning(): boolean {
    return this.authService.getUserRol() === this.rolType.ROLE_USER ||
      (this.authService.getUserRol() === this.rolType.ROLE_BDC && !this.isNotPlanning);
  }

  private checkRoleUser() {
    return this.authService.getUserRol();
  }

  private getPlanningEvents(filter) {
    filter.leadId = this.leadId;
    this.calendarComuService.searchPlanningEvents.emit(filter);
  }
}
